import { useMemo } from 'react';

import { Box, keyframes, List, ListItem } from '@chakra-ui/react';

import { SanityImage } from '~/components/sanity-image';

export type LogosCarouselProps = StaticLogosCarouselProps;

// Note: 52% is not a magic number... it's to ensure there is no skipping when looping.
const scrollAnimation = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-52%);
  }
`;

export function LogosCarousel({ logos }: LogosCarouselProps) {
  const duplicatedLogos = useMemo(() => [...logos, ...logos], [logos]);
  return (
    <Box
      maxWidth='1440px'
      mx='auto'
      overflow='clip'
      position='relative'
      py={6}
      _after={{
        background: 'linear-gradient(to left, white, transparent)',
        bottom: 0,
        content: '""',
        pointerEvents: 'none',
        position: 'absolute',
        right: 0,
        top: 0,
        width: {
          base: '20px',
          md: '80px',
        },
        zIndex: 1,
      }}
      _before={{
        background: 'linear-gradient(to right, white, transparent)',
        bottom: 0,
        content: '""',
        left: 0,
        pointerEvents: 'none',
        position: 'absolute',
        top: 0,
        width: {
          base: '20px',
          md: '80px',
        },
        zIndex: 1,
      }}
    >
      <List
        alignItems='center'
        animation={`${scrollAnimation} 45s linear infinite`}
        display='flex'
        gap='112px'
        justifyContent='flex-start'
        width='max-content'
        willChange='transform'
      >
        {duplicatedLogos.map(({ name, id, logo }, index) => (
          <ListItem
            key={`${id}-${index}`}
            flexShrink={0}
            _empty={{
              display: 'none',
            }}
          >
            {!!logo && (
              <SanityImage
                alt={name}
                image={logo}
                quality={75}
                style={{
                  height: '32px',
                  pointerEvents: 'none',
                }}
                priority
              />
            )}
          </ListItem>
        ))}
      </List>
    </Box>
  );
}

export type StaticLogosCarouselProps = Awaited<ReturnType<typeof transformStaticLogosCarouselProps>>;

export async function transformStaticLogosCarouselProps() {
  const { getCompanies } = await import('~/features/companies/controllers/get-companies');
  const logos = await getCompanies(['Siemens', 'Audi', 'Vodafone', 'Koc', 'Elm', 'Accenture', 'Telenor']);
  return {
    logos: logos.map((company) => ({
      id: company._id,
      name: company.name,
      logo: company.lofiLogoDark,
    })),
  };
}
